import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RideAttribution } from '../components/GarminAttribution.tsx';
import { PageShell } from '../components/PageShell.tsx';
import { ActivityCard } from '../components/ActivityItems.tsx';
import { RideScopeChips } from '../components/RideScopeChips.tsx';
import { RideFiltersBar } from '../components/RideFiltersBar.tsx';
import { ProviderIcon } from '../components/ProviderIcon.tsx';
import { LucideIcon } from '../components/icons/LucideIcon.tsx';
import { NO_FILTERS, isFiltered, useRideFilters, useRideProviders, type RideFilters } from '../hooks/useRideFilters.ts';
import { UploadRidesDialog } from '../components/UploadRidesDialog.tsx';
import { FitnessCard } from '../components/charts/FitnessCard.tsx';
import { PowerCurveCard } from '../components/charts/PowerCurveCard.tsx';
import { skeletonKeys } from '../lib/skeletonKeys.ts';
import { useAnalyzeRide, useRides, type RideScope } from '../hooks/useRides.ts';
import { useRideScope } from '../hooks/useRideScope.ts';
import { useNoRides } from '../hooks/useSetup.ts';
import { NoRidesCallout } from '../components/setup/NoRidesCallout.tsx';
import { SetupChecklist } from '../components/setup/SetupChecklist.tsx';
import { StaleBanner } from '../components/setup/StaleBanner.tsx';
import { isActivity } from '../lib/sports.ts';
import type { RideListItem } from '../types/api.ts';
import {
  formatDistanceKm,
  formatDuration,
  formatElevation,
  formatRideDate,
  formatRideDateShort,
  formatTss,
  rideTypeChip,
} from '../lib/rideFormat.ts';
import { RidesCalendar } from './RidesCalendar.tsx';
import { WeeklySummary } from './WeeklySummary.tsx';

type ViewMode = 'list' | 'calendar';
const VIEW_MODE_KEY = 'rides_view_mode';

function loadViewMode(): ViewMode {
  // Default to ``calendar`` for first-time visits — it surfaces more
  // ride context (week / month rollups inline with the grid) than the
  // list does. Users who explicitly toggled to ``list`` previously
  // keep their choice via the persisted localStorage value.
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY);
    return v === 'list' ? 'list' : 'calendar';
  } catch {
    return 'calendar';
  }
}

export function Dashboard() {
  const { t } = useTranslation();
  const { enabled: activitiesEnabled, scope, setScope } = useRideScope();
  const [viewMode, setViewMode] = useState<ViewMode>(loadViewMode);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filters, setFilters] = useRideFilters();
  const providers = useRideProviders();
  const noRides = useNoRides();

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_KEY, viewMode);
    } catch {
      // localStorage unavailable (private browsing) — fall back to
      // session-only state, no error to surface.
    }
  }, [viewMode]);

  return (
    <PageShell
      title={t('dashboard.title')}
      subtitle={t('dashboard.subtitle')}
    >
      <div className="flex flex-col gap-4 mb-4">
        <SetupChecklist />
        <StaleBanner />
        {noRides && <NoRidesCallout />}
        <FitnessCard />
        <PowerCurveCard />
      </div>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <ViewToggle mode={viewMode} onChange={setViewMode} />
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className="mb-4 px-4 py-1.5 rounded-lg text-sm font-semibold bg-green-400 text-gray-900 hover:bg-green-300 transition-colors"
        >
          {t('upload.button')}
        </button>
      </div>
      {activitiesEnabled && <RideScopeChips scope={scope} onChange={setScope} />}
      {!noRides && <RideFiltersBar filters={filters} onChange={setFilters} providers={providers} scope={scope} />}
      {uploadOpen && <UploadRidesDialog onClose={() => setUploadOpen(false)} />}

      {viewMode === 'calendar' ? (
        <RidesCalendar scope={scope} filters={filters} />
      ) : (
        <RidesListView scope={scope} noRides={noRides} filters={filters} onClearFilters={() => setFilters(NO_FILTERS)} />
      )}
    </PageShell>
  );
}

/** The latest ride (activities never take the hero: it is ride-only) and the rest of the list. */
function splitLatestRide(items: RideListItem[]): { latest: RideListItem | null; rest: RideListItem[] } {
  const latest = items.find((r) => !isActivity(r)) ?? null;
  return { latest, rest: items.filter((r) => r !== latest) };
}

function RidesListView({ scope, noRides, filters, onClearFilters }: Readonly<{
  scope: RideScope;
  noRides: boolean;
  filters: RideFilters;
  onClearFilters: () => void;
}>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useRides(20, scope, filters);
  const filtered = isFiltered(filters);
  const items = data?.rides ?? [];
  const { latest, rest } = splitLatestRide(items);
  const loaded = !isLoading && !isError;

  return (
    <>
      {/* List mode leads with the latest-ride hero + this-week
          summary stack. Calendar mode skips both — its month grid +
          per-week strips + month-level header already surface the
          same numbers, and a duplicate top-of-page summary just
          pushes the calendar below the fold. */}
      {loaded && latest && (
        <div className="mb-4">
          <LatestRideHero
            ride={latest}
            onOpen={() => navigate(`/rides/${latest.id}`)}
          />
        </div>
      )}
      <div className="mb-4">
        <WeeklySummary />
      </div>

      {isLoading && <RidesSkeleton />}

      {isError && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 text-sm flex items-center justify-between">
          <span>{t('dashboard.errorLoading')}</span>
          <button
            onClick={() => refetch()}
            className="underline text-red-300 hover:text-red-200"
          >
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {/* With no rides at all the connect call to action above says it. */}
      {loaded && items.length === 0 && !noRides && (
        filtered ? <FiltersEmpty onClear={onClearFilters} /> : <RidesEmpty scope={scope} />
      )}

      {!isLoading && rest.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
            {t(scope === 'all' ? 'activities.previous' : 'dashboard.previousRides')}
          </h2>
          <ul className="flex flex-col gap-3">
            {rest.map((item) => (
              <li key={item.id}>
                <ListItemCard
                  item={item}
                  onClick={() => navigate(`/rides/${item.id}`)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function ListItemCard({
  item,
  onClick,
}: Readonly<{ item: RideListItem; onClick: () => void }>) {
  return isActivity(item) ? (
    <ActivityCard item={item} onClick={onClick} />
  ) : (
    <RideCard ride={item} onClick={onClick} />
  );
}

function FiltersEmpty({ onClear }: Readonly<{ onClear: () => void }>) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-8 text-center">
      <h2 className="text-lg font-semibold text-(--color-text-primary)">{t('sources.filter.noMatch')}</h2>
      <button type="button" onClick={onClear} className="mt-3 text-sm text-(--color-accent) hover:underline">
        {t('sources.filter.clearFilters')}
      </button>
    </div>
  );
}

/** Indoor or outdoor mark in the ride's date line. */
function WhereIcon({ indoor }: Readonly<{ indoor: boolean | null | undefined }>) {
  return <LucideIcon name={indoor ? 'house' : 'bike'} size={14} />;
}

function RidesEmpty({ scope }: Readonly<{ scope: RideScope }>) {
  const { t } = useTranslation();
  const all = scope === 'all';
  return (
    <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-8 text-center">
      <span className="text-5xl">{all ? '\u{1F3C5}' : '\u{1F6B4}'}</span>
      <h2 className="text-lg font-semibold text-(--color-text-primary) mt-3">
        {t(all ? 'activities.emptyAllTitle' : 'dashboard.emptyTitle')}
      </h2>
      <p className="text-(--color-text-secondary) mt-2 text-sm">
        {t(all ? 'activities.emptyAllBody' : 'dashboard.emptyBody')}
      </p>
    </div>
  );
}

function ViewToggle({
  mode,
  onChange,
}: Readonly<{ mode: ViewMode; onChange: (m: ViewMode) => void }>) {
  const { t } = useTranslation();
  const buttonClass = (m: ViewMode) =>
    `flex-1 sm:flex-none px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
      mode === m
        ? 'bg-(--color-accent)/15 text-(--color-accent) border-(--color-accent)/40'
        : 'bg-(--color-bg-tertiary) text-(--color-text-secondary) border-(--color-border) hover:border-gray-400'
    }`;

  return (
    <div className="flex gap-2 mb-4">
      {/* Calendar first since it's the default tab. List on the right
          so the order matches the new-user experience: open page →
          calendar shows → optionally toggle to list. */}
      <button
        onClick={() => onChange('calendar')}
        className={buttonClass('calendar')}
      >
        📅 {t('dashboard.viewCalendar')}
      </button>
      <button onClick={() => onChange('list')} className={buttonClass('list')}>
        ☰ {t('dashboard.viewList')}
      </button>
    </div>
  );
}

function LatestRideHero({
  ride,
  onOpen,
}: Readonly<{ ride: RideListItem; onOpen: () => void }>) {
  const { t } = useTranslation();
  const analyzeMut = useAnalyzeRide();
  const [analyzeError, setAnalyzeError] = useState('');
  const chip = rideTypeChip(ride.ride_type);

  const summary = analyzeMut.data?.summary ?? ride.coach_summary;
  const summaryAt = analyzeMut.data?.generated_at ?? ride.coach_summary_at;

  const handleAnalyze = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnalyzeError('');
    try {
      await analyzeMut.mutateAsync(ride.id);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : t('dashboard.analyzeFailed');
      setAnalyzeError(msg);
    }
  };
  const analyzeLabel = summary
    ? `🤖 ${t('dashboard.regenerate')}`
    : `🤖 ${t('dashboard.analyzeRide')}`;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
        {t('dashboard.latestRideTitle')}
      </h2>
      <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-accent)/40 p-5 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-(--color-text-secondary)">
              <WhereIcon indoor={ride.is_indoor} />
              <span>{formatRideDate(ride.started_at)}</span>
              <ProviderIcon provider={ride.provider} size={14} />
            </div>
            <h3 className="text-lg font-semibold text-(--color-text-primary) mt-0.5 truncate">
              {ride.name ?? 'Ride'}
            </h3>
            <RideAttribution text={ride.attribution} />
          </div>
          <span
            className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium border ${chip.className}`}
          >
            {chip.label}
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <HeroStat
            label={t('dashboard.statDuration')}
            value={formatDuration(ride.duration_sec)}
          />
          <HeroStat
            label={t('dashboard.statDistance')}
            value={formatDistanceKm(ride.distance_m)}
          />
          <HeroStat
            label={t('dashboard.statAvgPower')}
            value={ride.avg_power == null ? '—' : `${ride.avg_power} W`}
          />
          <HeroStat
            label="TSS"
            value={formatTss(ride)}
          />
          <HeroStat
            label="NP"
            value={
              ride.normalized_power == null ? '—' : `${ride.normalized_power} W`
            }
          />
          <HeroStat
            label={t('dashboard.statAvgHr')}
            value={ride.avg_hr == null ? '—' : `${ride.avg_hr} bpm`}
          />
          <HeroStat
            label={t('dashboard.statClimb')}
            value={formatElevation(ride.elevation_gain_m)}
          />
          <HeroStat
            label="IF"
            value={
              ride.intensity_factor == null
                ? '—'
                : ride.intensity_factor.toFixed(2)
            }
          />
        </div>

        <div className="flex flex-col gap-2 border-t border-(--color-border) pt-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h4 className="text-xs text-(--color-text-secondary) uppercase tracking-wide">
              {t('dashboard.coachSummaryTitle')}
            </h4>
            <button
              onClick={handleAnalyze}
              disabled={analyzeMut.isPending}
              className="px-3 py-1 rounded-lg bg-(--color-accent)/15 text-(--color-accent) hover:bg-(--color-accent)/25 transition-colors text-xs font-medium border border-(--color-accent)/30 disabled:opacity-50"
            >
              {analyzeMut.isPending ? t('dashboard.analyzing') : analyzeLabel}
            </button>
          </div>
          {analyzeError && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 text-xs">
              {analyzeError}
            </div>
          )}
          {summary ? (
            <div className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-3 py-3 text-sm text-(--color-text-primary) whitespace-pre-wrap leading-relaxed">
              {summary}
            </div>
          ) : (
            <p className="text-xs text-(--color-text-secondary)">
              {t('dashboard.noSummaryYet')}
            </p>
          )}
          {summaryAt && (
            <p className="text-[11px] text-(--color-text-secondary)">
              {t('dashboard.generatedAt', {
                when: new Date(summaryAt).toLocaleString(),
              })}
            </p>
          )}
        </div>

        <button
          onClick={onOpen}
          className="self-start text-xs text-(--color-accent) hover:underline"
        >
          {t('dashboard.openRide')} {'→'}
        </button>
      </div>
    </section>
  );
}

function HeroStat({
  label,
  value,
}: Readonly<{ label: string; value: string }>) {
  return (
    <div className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) p-2 text-center">
      <div className="text-[10px] text-(--color-text-secondary) uppercase tracking-wide">
        {label}
      </div>
      <div className="text-(--color-text-primary) font-semibold">{value}</div>
    </div>
  );
}

function RideCard({
  ride,
  onClick,
}: Readonly<{ ride: RideListItem; onClick: () => void }>) {
  const chip = rideTypeChip(ride.ride_type);

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl bg-(--color-bg-secondary) border border-(--color-border) hover:border-(--color-accent)/50 hover:bg-(--color-bg-tertiary)/40 transition-colors p-4 flex flex-col gap-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-(--color-text-secondary) text-xs">
            <WhereIcon indoor={ride.is_indoor} />
            <span>{formatRideDateShort(ride.started_at)}</span>
          </div>
          <h3 className="text-base font-semibold text-(--color-text-primary) truncate mt-0.5">
            {ride.name ?? 'Ride'}
          </h3>
          <RideAttribution text={ride.attribution} />
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium border ${chip.className}`}
          >
            {chip.label}
          </span>
          <ProviderIcon provider={ride.provider} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <Stat label="Duration" value={formatDuration(ride.duration_sec)} />
        <Stat label="Distance" value={formatDistanceKm(ride.distance_m)} />
        <Stat
          label="Avg Power"
          value={ride.avg_power == null ? '—' : `${ride.avg_power} W`}
        />
        <Stat
          label="TSS"
          value={formatTss(ride)}
        />
        <Stat
          label="NP"
          value={
            ride.normalized_power == null ? '—' : `${ride.normalized_power} W`
          }
        />
        <Stat
          label="Avg HR"
          value={ride.avg_hr == null ? '—' : `${ride.avg_hr} bpm`}
        />
        <Stat label="Climb" value={formatElevation(ride.elevation_gain_m)} />
        <Stat
          label="IF"
          value={
            ride.intensity_factor == null
              ? '—'
              : ride.intensity_factor.toFixed(2)
          }
        />
      </div>
    </button>
  );
}

function Stat({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div>
      <div className="text-[11px] text-(--color-text-secondary) uppercase tracking-wide">
        {label}
      </div>
      <div className="text-(--color-text-primary) font-medium">{value}</div>
    </div>
  );
}

const RIDES_SKELETON_KEYS = skeletonKeys(4);

function RidesSkeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      {RIDES_SKELETON_KEYS.map((key) => (
        <div
          key={key}
          className="h-32 rounded-xl bg-(--color-bg-secondary) border border-(--color-border)"
        />
      ))}
    </div>
  );
}
